import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '@/lib/api'
import { formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Modal, Select } from '@/components/ui/primitives'
import { t } from '@/i18n'

/** What is being paid off. Null closes the dialog. */
export interface Settleable {
  id: number
  /** Shown in the title, so it is obvious which bill is about to be paid. */
  what: string
  amountUsd: number
}

/**
 * Pays off something the shop owes, out of somewhere the shop has.
 *
 * Shared by deliveries and expenses because it is one question — a supplier invoice and an
 * electricity bill are settled the same way, and having two dialogs would eventually mean two
 * answers to "does this come out of the till".
 *
 * The till option needs an open shift, since a drawer pay-out has to land on a shift to be
 * reconciled at close. Where none is open the option says so rather than failing on submit.
 */
export function SettleDialog({
  target,
  endpoint,
  invalidate,
  onClose,
}: {
  target: Settleable | null
  /** Called as `${endpoint}/${id}/...` — the caller owns the shape of the path. */
  endpoint: (id: number) => string
  /** Query keys to refresh once the money has moved. */
  invalidate: string[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [paidFrom, setPaidFrom] = useState('CashDrawer')
  const [error, setError] = useState<string | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () =>
      apiClient.get<{ id: number; name: string; currentShiftId: number | null }[]>('/api/registers'),
    enabled: target !== null,
  })

  const openShiftId =
    registers.data?.find((register) => register.currentShiftId !== null)?.currentShiftId ?? null

  useEffect(() => {
    if (target) {
      // The till is the common answer in a shop, so it leads — unless there is no till open, in
      // which case offering it first would only be something to correct.
      setPaidFrom(openShiftId === null ? 'Safe' : 'CashDrawer')
      setError(null)
    }
  }, [target, openShiftId])

  const settle = useMutation({
    mutationFn: () =>
      apiClient.post(endpoint(target!.id), {
        paidFrom,
        shiftId: paidFrom === 'CashDrawer' ? openShiftId : null,
        paidDate: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: async () => {
      await Promise.all(
        // Everything the money touched: what is owed, the drawer, the safe, and the list the
        // row came from.
        [...invalidate, 'payables', 'shift-expected', 'shift-statement', 'safe', 'safe-history'].map(
          (key) => queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      )
      onClose()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not settle that.',
      ),
  })

  const blocked = paidFrom === 'CashDrawer' && openShiftId === null

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={t('Pay this off')}
      description={target ? `${target.what} · ${formatUsd(target.amountUsd)}` : ''}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => settle.mutate()} disabled={settle.isPending || blocked}>
            {settle.isPending ? 'Paying…' : 'Pay it'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label={t('Paid from')}
          value={paidFrom}
          onChange={(event) => setPaidFrom(event.target.value)}
        >
          <option value="CashDrawer">{t('The till')}</option>
          <option value="Safe">{t('The safe')}</option>
          <option value="Bank">{t('Bank transfer')}</option>
          <option value="Card">{t('Card')}</option>
        </Select>

        <p className="surface-sunken rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
          {blocked
            ? 'No till is open, so there is nowhere to take the money from. Open one, or pay another way.'
            : paidFrom === 'CashDrawer'
              ? `${formatUsd(target?.amountUsd ?? 0)} comes out of the open till, and the drawer count at close expects that much less.`
              : paidFrom === 'Safe'
                ? `${formatUsd(target?.amountUsd ?? 0)} comes out of the safe.`
                : 'Recorded as paid. Neither the till nor the safe is touched.'}
        </p>

        {error && (
          <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
